import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { FadeUp, MaskLines } from "../components/Reveal";

const PORTRAIT_IMG = "https://images.pexels.com/photos/6615203/pexels-photo-6615203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STUDIO_IMG = "https://images.unsplash.com/photo-1649688066826-947419c66e5c?crop=entropy&cs=srgb&fm=jpg&q=85";
const DETAIL_IMG = "https://images.pexels.com/photos/17885652/pexels-photo-17885652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  const [portraitSrc, setPortraitSrc] = useState(PORTRAIT_IMG);

  useEffect(() => {
    api.get("/settings").then((r) => r.data?.principal_photo && setPortraitSrc(r.data.principal_photo)).catch(() => {});
  }, []);

  return (
    <main className="pt-28 sm:pt-40 px-6 sm:px-10 pb-24 sm:pb-32">
      <div className="max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
            <span className="h-px w-10 bg-[#C29B38]" /> The Studio
          </p>
        </FadeUp>
        <MaskLines
          as="h1"
          delay={0.1}
          className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05]"
          lines={["Architecture, drawn", { node: <span><em className="italic">in pencil first</em>.</span> }]}
        />

        <div className="mt-16 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <FadeUp>
              <div className="frame-clip img-zoom bg-[#EFECE6]">
                <img src={portraitSrc} alt="Ak — Principal Architect" className="w-full aspect-[4/5] object-cover" />
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A857D]">Fig. 02 — Ak, Principal Architect</p>
            </FadeUp>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <FadeUp>
              <blockquote className="font-serif italic text-2xl sm:text-3xl leading-snug">
                “A building should feel inevitable — as if the site had been waiting for exactly this wall, this window, this shade of plaster.”
              </blockquote>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="mt-10 flex flex-col gap-6 font-light text-[#57534E] text-lg leading-relaxed">
                <p>
                  Space Arts by Ak is a small architecture and interior design studio that works personally on every commission. We design across scales — a private villa, a gallery pavilion, a single room — but the intent never changes: quiet architecture that lets light, material and proportion do the talking.
                </p>
                <p>
                  The studio stays deliberately small. Nothing is drawn by strangers, nothing is detailed twice, and every project is visited on site as often as it takes. We would rather complete three buildings we love in a year than thirty we don't.
                </p>
                <p>
                  Our clients come to us for calm — and stay for the details: a brass reveal, a sunbeam kept in mind since the first sketch, a threshold where snow and warm air meet.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.25}>
              <Link
                to="/contact"
                data-testid="about-cta"
                className="mt-10 inline-flex items-center gap-3 bg-[#181816] text-[#F8F6F0] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] transition-colors duration-300"
              >
                Work with the Studio <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeUp>
          </div>
        </div>

        <div className="mt-28">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">The Craft</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-5xl tracking-tight">Pencil, plaster, patience.</h2>
          </FadeUp>
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <FadeUp>
              <div className="frame-clip img-zoom bg-[#EFECE6]">
                <img src={STUDIO_IMG} alt="The Space Arts design atelier — drafting tables and material samples" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A857D]">Fig. 03 — The Atelier</p>
            </FadeUp>
            <FadeUp delay={0.15} className="md:mt-16">
              <div className="frame-clip img-zoom bg-[#EFECE6]">
                <img src={DETAIL_IMG} alt="Afternoon light across a plaster wall" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A857D]">Fig. 04 — Light Study II</p>
            </FadeUp>
          </div>
        </div>

        <FadeUp>
          <div className="mt-24 border-t hairline pt-14 grid md:grid-cols-3 gap-10">
            {[
              ["Drawn by hand", "Every project begins as a pencil section before it becomes a file."],
              ["Built with makers", "Long-standing relationships with stonemasons, joiners and plasterers."],
              ["Kept for years", "We revisit finished projects — and design them to grow more beautiful."],
            ].map(([t, b]) => (
              <div key={t}>
                <h3 className="font-serif text-2xl">{t}</h3>
                <p className="mt-3 font-light text-[#57534E] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </main>
  );
}
